import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useMaintenance } from '../../../contexts/MaintenanceContext';
import '../../../css/common/Maintenance.css';

function formatEta(rawEta) {
  if (!rawEta) return null;
  try {
    const value = String(rawEta);
    const [datePart, timePart] = value.split('T');
    if (!datePart) return null;
    const time = (timePart || '').slice(0, 5); // HH:MM まで
    return time ? `${datePart} ${time}` : datePart;
  } catch {
    return null;
  }
}

function buildMessage(maintenance) {
  const eta = formatEta(maintenance?.eta);
  const custom = (maintenance?.message || '').trim();
  const base = custom || '現在メンテナンス中です。';
  return eta ? `${base} 終了予定: ${eta}` : base;
}

function MaintenanceBanner() {
  const { maintenance, maintenanceLoading, isMaintenanceActive } = useMaintenance();
  const location = useLocation();
  const isTimetableView = location.pathname.includes('/timetables/');
  const bannerRef = useRef(null);

  // バナー高さを CSS 変数に反映し、固定高レイアウトで高さを差し引けるようにする
  useEffect(() => {
    if (isTimetableView) {
      document.documentElement.style.setProperty('--maintenance-offset', '0px');
      return undefined;
    }
    // 非表示時は必ず 0 に戻す
    if (!isMaintenanceActive) {
      document.documentElement.style.setProperty('--maintenance-offset', '0px');
      return undefined;
    }
    const el = bannerRef.current;
    if (!el) return undefined;
    const updateOffset = () => {
      const h = el.offsetHeight || 0;
      document.documentElement.style.setProperty('--maintenance-offset', `${h}px`);
    };
    const resizeObserver = new ResizeObserver(updateOffset);
    resizeObserver.observe(el);
    window.addEventListener('resize', updateOffset);
    updateOffset();
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateOffset);
      document.documentElement.style.setProperty('--maintenance-offset', '0px');
    };
  }, [isMaintenanceActive, maintenance, isTimetableView]);

  // Dev Portal 自身では帯を出さない（/dev 配下は運用者向け）
  if (location.pathname.startsWith('/dev')) return null;
  if (isTimetableView) return null;

  if (maintenanceLoading) return null;
  if (!isMaintenanceActive) return null;

  const message = buildMessage(maintenance);
  if (!message) return null;

  return (
    <div className="maintenance-banner" role="status" aria-live="polite" ref={bannerRef}>
      <div className="maintenance-banner__inner">
        <span className="maintenance-banner__label">メンテナンス中</span>
        <div className="maintenance-banner__track" aria-label={message}>
          <div className="maintenance-banner__scroll">
            <span className="maintenance-banner__message">{message}</span>
            <span className="maintenance-banner__message" aria-hidden="true">
              {message}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MaintenanceBanner;
